import type { MobileFrontEndGalleryView } from '@/shared/mobile-service/api/cors-mobile-api';
import { storageUrlForDisplay } from '@/shared/storageUrlForDisplay';
import { humanize } from '@/shared/mobile-service/lib/columns';
import { resolveGalleryAsset } from '@/shared/mobile-service/lib/build-gallery-query';

/** One field/value row shown in a map point popup. */
export interface PointDetailEntry {
  label: string;
  value: string;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function displayValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Build ordered popup detail entries from a row for the given (possibly dotted) field paths. */
export function buildPointDetailEntries(
  row: Record<string, unknown>,
  fieldNames: string[],
  resolvePath: (row: Record<string, unknown>, path: string) => unknown,
): PointDetailEntry[] {
  return fieldNames
    .map((field) => ({
      label: humanize(field.split('.').pop() ?? field),
      value: displayValue(resolvePath(row, field)),
    }))
    .filter((entry) => entry.value !== '');
}

/**
 * Fetchable image or video behind a pin, when the gallery names one.
 *
 * Audio stays in {@link pointMediaMarkup}. A device-local path is not a URL
 * the browser can load, so those pins stay text-only. A missing original
 * falls back to the thumbnail file.
 */
export function pointMediaTarget(
  row: Record<string, unknown>,
  gallery: MobileFrontEndGalleryView | undefined,
): { url: string; mime: string | null; kind: 'image' | 'video' } | null {
  if (!gallery) return null;
  const asset = resolveGalleryAsset(row, gallery);
  if (!asset || (asset.kind !== 'image' && asset.kind !== 'video')) return null;
  return { url: asset.url, mime: asset.mime, kind: asset.kind };
}

/** Fetchable audio URL for a pin, when the gallery names one. */
export function pointAudioUrl(
  row: Record<string, unknown>,
  gallery: MobileFrontEndGalleryView | undefined,
): string | null {
  if (!gallery) return null;
  const asset = resolveGalleryAsset(row, gallery);
  return asset?.kind === 'audio' ? asset.url : null;
}

export function pointMediaMarkup(
  row: Record<string, unknown>,
  gallery: MobileFrontEndGalleryView | undefined,
): string {
  if (!gallery) return '';
  const asset = resolveGalleryAsset(row, gallery);
  if (!asset) return '';
  const src = escapeHtml(storageUrlForDisplay(asset.url));
  if (asset.kind === 'image') {
    return `<img class="mv-point-media" src="${src}" alt="" onerror="this.remove()" />`;
  }
  if (asset.kind === 'video') {
    return `<video class="mv-point-media" controls playsinline preload="metadata" src="${src}"></video>`;
  }
  if (asset.kind === 'audio') {
    return `<audio class="mv-point-media" controls preload="metadata" src="${src}"></audio>`;
  }
  return '';
}

/** Render the HTML shown inside a MapLibre popup for one point. */
export function renderPointPopupHtml(args: {
  title: string | null;
  timestamp: string | null;
  details: PointDetailEntry[];
  latitude: number;
  longitude: number;
  mediaHtml?: string;
  /** When set, a React preview is mounted into this node after the popup opens. */
  mediaHost?: boolean;
}): string {
  const { title, timestamp, details, latitude, longitude, mediaHtml, mediaHost } = args;
  const rows: string[] = [];
  if (mediaHost) {
    rows.push('<div data-point-media-host></div>');
  } else if (mediaHtml) {
    rows.push(mediaHtml);
  }
  if (title) {
    rows.push(`<div class="mv-point-title">${escapeHtml(title)}</div>`);
  }
  if (timestamp) {
    rows.push(`<div class="mv-point-timestamp">${escapeHtml(timestamp)}</div>`);
  }
  for (const entry of details) {
    rows.push(
      `<div class="mv-point-row"><span class="mv-point-key">${escapeHtml(
        entry.label,
      )}</span><span class="mv-point-val">${escapeHtml(entry.value)}</span></div>`,
    );
  }
  rows.push(
    `<div class="mv-point-coords">${escapeHtml(
      `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
    )}</div>`,
  );
  return `<div class="mv-point-popup">${rows.join('')}</div>`;
}
